import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import config from "../config";

const { api } = config;

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// content type
const authUser: any = sessionStorage.getItem("authUser")
const token = JSON.parse(authUser) ? JSON.parse(authUser).token : null;
if (token) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  console.log('Setting auth token in axios defaults:', token);
}

// Helper function to handle logout
const handleLogout = () => {
  sessionStorage.removeItem("authUser");
  delete axios.defaults.headers.common["Authorization"];
  // Redirect to login page
  window.location.href = "/login";
};

// Request interceptor to log requests
axios.interceptors.request.use(
  function (config) {
    console.log('Making API request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      headers: config.headers,
      data: config.data
    });
    return config;
  },
  function (error) {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    const status = error.response ? error.response.status : error.status;
    
    // Log the error for debugging
    console.error('API Error:', {
      status: status,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      headers: error.config?.headers
    });
    
    switch (status) {
      case 403:
        // JWT token expired or unauthorized access
        // Check if this is an actual authentication error or just a permission issue
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.includes('token') || errorMessage.includes('authentication') || errorMessage.includes('expired')) {
          message = "Session expired. Please login again.";
          handleLogout();
        } else {
          // This might be a permission issue, not an authentication issue
          message = error.response?.data?.message || "Access denied";
        }
        break;
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        // Check for specific token expiration response
        const errorData = error.response?.data || {};
        const authErrorMessage = errorData.message || '';
        
        if (errorData.error === 'TOKEN_EXPIRED' || authErrorMessage.includes('expired')) {
          message = "Your session has expired. Please login again.";
          console.log('Token expired - redirecting to login');
          handleLogout();
        } else if (authErrorMessage.includes('User not found') || authErrorMessage.includes('Invalid credentials')) {
          message = "Session expired. Please login again.";
          handleLogout();
        } else {
          message = errorData.message || "Authentication failed";
        }
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        message = error.message || error;
    }
    return Promise.reject(error); // Return the full error object instead of just the message
  }
);
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token : string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Fetches data from the given URL
   */
  get = (url: string, params?: any): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * Posts the given data to the URL
   */
  create = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.post(url, data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser, handleLogout };